import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { 
  subscriptionService, 
  SubscriptionType, 
  SubscriptionLimits, 
  SUBSCRIPTION_LIMITS 
} from '../services/subscription/subscription.service';
import { LockClosedIcon } from '@heroicons/react/24/outline';
import { Button } from '../components/shared/Button';
import { getAuth, onAuthStateChanged } from 'firebase/auth';

// Interface pour le contexte d'abonnement
interface SubscriptionContextType {
  subscription: SubscriptionType;
  limits: SubscriptionLimits;
  loading: boolean;
  hasFeature: (feature: keyof SubscriptionLimits['features']) => boolean;
  checkLimit: (limitType: keyof Omit<SubscriptionLimits, 'type' | 'features'>, currentCount: number) => 
    Promise<{ allowed: boolean; maxAllowed: number }>;
  updateSubscription: (userId: string, newSubscription: SubscriptionType) => Promise<void>;
  isUpgradeRequired: (feature?: keyof SubscriptionLimits['features'], minSubscription?: SubscriptionType) => boolean;
  refreshSubscription: () => Promise<void>;
}

// Création du contexte avec une valeur par défaut
const SubscriptionContext = createContext<SubscriptionContextType>({
  subscription: 'free',
  limits: SUBSCRIPTION_LIMITS.free,
  loading: true,
  hasFeature: () => false,
  checkLimit: async () => ({ allowed: false, maxAllowed: 0 }),
  updateSubscription: async () => {},
  isUpgradeRequired: () => false,
  refreshSubscription: async () => {}
});

// Hook personnalisé pour utiliser le contexte d'abonnement
export const useSubscription = () => useContext(SubscriptionContext);

// Composant provider du contexte
export const SubscriptionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [subscription, setSubscription] = useState<SubscriptionType>('free');
  const [limits, setLimits] = useState<SubscriptionLimits>(SUBSCRIPTION_LIMITS.free);
  const [loading, setLoading] = useState<boolean>(true);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);

  // Fonction pour charger l'abonnement
  const loadSubscription = async (userId: string, retryCount = 0) => {
    try {
      setLoading(true);

      const currentSub = await subscriptionService.getCurrentSubscription(userId);

      setSubscription(currentSub.type);
      setLimits(currentSub);

      // Stocker dans localStorage pour persistance
      localStorage.setItem('userSubscription', JSON.stringify({
        type: currentSub.type,
        userId: userId,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('❌ Erreur lors du chargement de l\'abonnement:', error);
      
      // En cas d'erreur, essayer de récupérer depuis localStorage si disponible
      const cachedSubscription = localStorage.getItem('userSubscription');
      if (cachedSubscription && retryCount === 0) {
        try {
          const cached = JSON.parse(cachedSubscription);
          // Vérifier que le cache correspond à l'utilisateur actuel et n'est pas trop ancien (24h)
          if (cached.userId === userId && (Date.now() - cached.timestamp) < 24 * 60 * 60 * 1000) {
            setSubscription(cached.type as SubscriptionType);
            setLimits(SUBSCRIPTION_LIMITS[cached.type as SubscriptionType]);

            // Essayer de recharger en arrière-plan
            setTimeout(() => loadSubscription(userId, 1), 2000);
            return;
          }
        } catch (cacheError) {
          console.warn('Erreur lors de la lecture du cache:', cacheError);
        }
      }
      
      // Utiliser l'abonnement gratuit par défaut
      setSubscription('free');
      setLimits(SUBSCRIPTION_LIMITS.free);
    } finally {
      setLoading(false);
    }
  };

  // Écouter les changements d'authentification
  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = onAuthStateChanged(getAuth(), async (user) => {
      if (!isMounted) return;
      
      if (user) {
        setCurrentUserId(user.uid);
        await loadSubscription(user.uid);
      } else {
        setCurrentUserId(null);
        setSubscription('free');
        setLimits(SUBSCRIPTION_LIMITS.free);
        setLoading(false);
        localStorage.removeItem('userSubscription');
      }
    });
    
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Fonction pour rafraîchir l'abonnement
  const refreshSubscription = async () => {
    if (currentUserId) {
      await loadSubscription(currentUserId);
    }
  };

  // Vérifier si l'utilisateur a accès à une fonctionnalité spécifique
  const hasFeature = (feature: keyof SubscriptionLimits['features']): boolean => {
    return limits.features[feature];
  };

  // Vérifier si l'utilisateur a atteint la limite pour une ressource
  const checkLimit = async (
    limitType: keyof Omit<SubscriptionLimits, 'type' | 'features'>,
    currentCount: number
  ): Promise<{ allowed: boolean; maxAllowed: number }> => {
    const auth = getAuth();
    const currentUser = auth.currentUser;
    
    if (!currentUser) {
      return { allowed: false, maxAllowed: SUBSCRIPTION_LIMITS.free[limitType] as number };
    }
    
    const result = await subscriptionService.checkLimit(currentUser.uid, limitType, currentCount);
    return { allowed: !result.hasReachedLimit, maxAllowed: result.max };
  };

  // Mettre à jour l'abonnement
  const updateSubscription = async (userId: string, newSubscription: SubscriptionType): Promise<void> => {
    try {
      await subscriptionService.updateSubscription(userId, newSubscription);

      // Mettre à jour l'état local
      setSubscription(newSubscription);
      setLimits(SUBSCRIPTION_LIMITS[newSubscription]);

      // Mettre à jour le cache
      localStorage.setItem('userSubscription', JSON.stringify({
        type: newSubscription,
        userId: userId,
        timestamp: Date.now()
      }));
    } catch (error) {
      console.error('❌ Erreur lors de la mise à jour de l\'abonnement:', error);
      throw error;
    }
  };

  // Vérifier si une mise à niveau est nécessaire
  const isUpgradeRequired = (
    feature?: keyof SubscriptionLimits['features'],
    minSubscription?: SubscriptionType
  ): boolean => {
    if (loading) return false;
    
    if (minSubscription) {
      const subscriptionRank = { free: 0, essential: 1, pro: 2 };
      return subscriptionRank[subscription] < subscriptionRank[minSubscription];
    }
    
    if (feature) {
      return !limits.features[feature];
    }
    
    return false;
  };

  const value = React.useMemo(() => ({
    subscription,
    limits,
    loading,
    hasFeature,
    checkLimit,
    updateSubscription,
    isUpgradeRequired,
    refreshSubscription
  }), [subscription, limits, loading]);

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  );
};

// HOC pour restreindre l'accès aux fonctionnalités premium
export function withSubscriptionRestriction<P>(
  WrappedComponent: React.ComponentType<P>,
  requiredFeature?: keyof SubscriptionLimits['features'],
  requiredSubscription?: SubscriptionType
) {
  return function WithSubscriptionComponent(props: P) {
    const { subscription, hasFeature, loading, isUpgradeRequired } = useSubscription();
    const [hasAccess, setHasAccess] = useState<boolean>(false);
    const { t } = useTranslation();
    const navigate = useNavigate();
    
    useEffect(() => {
      if (loading) return;
      
      if (requiredSubscription) {
        const subscriptionRank = { free: 0, essential: 1, pro: 2 };
        setHasAccess(subscriptionRank[subscription] >= subscriptionRank[requiredSubscription]);
      } else if (requiredFeature) {
        setHasAccess(hasFeature(requiredFeature));
      } else {
        setHasAccess(true);
      }
    }, [subscription, loading]);
    
    if (loading) {
      return <div className="text-center p-4">
        <p>{t('common.loading')}</p>
      </div>;
    }
    
    if (!hasAccess) {
      return (
        <div className="text-center p-6 bg-gray-50 dark:bg-gray-800 rounded-lg shadow-md">
          <LockClosedIcon className="h-12 w-12 mx-auto text-primary-500 mb-4" />
          <h2 className="text-xl font-semibold mb-2">
            {t('featureRestricted', { ns: 'subscription' })}
          </h2>
          <p className="mb-4 text-gray-600 dark:text-gray-300">
            {requiredFeature 
              ? t('subscription.upgradeToAccessFeature', { feature: t('subscription.features.' + requiredFeature) })
              : t('subscription.upgradeToAccessPlan', { plan: t('subscription.plans.' + requiredSubscription) })
            }
          </p>
          <Button 
            variant="primary"
            onClick={() => navigate('/subscription')}
          >
            {t('viewPlans', { ns: 'subscription' })}
          </Button>
        </div>
      );
    }
    
    return <WrappedComponent {...props} />;
  };
}

// Composant d'alerte de limite atteinte
export const LimitReachedAlert: React.FC<{
  limitType: keyof Omit<SubscriptionLimits, 'features'>;
  currentCount: number;
  onUpgrade?: () => void;
}> = ({ limitType, currentCount, onUpgrade }) => {
  const { checkLimit, subscription } = useSubscription();
  const { t } = useTranslation();
  const [limitInfo, setLimitInfo] = useState<{ allowed: boolean; maxAllowed: number } | null>(null);

  useEffect(() => {
    const checkLimitInfo = async () => {
      const info = await checkLimit(limitType, currentCount);
      setLimitInfo(info);
    };
    
    checkLimitInfo();
  }, [limitType, currentCount, subscription]);

  if (!limitInfo || limitInfo.allowed) {
    return null;
  }

  return (
    <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4 mb-4">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-200">
            {t('subscription.limitReached', { 
              limit: t('subscription.limits.' + limitType),
              count: currentCount,
              max: limitInfo.maxAllowed
            })}
          </h3>
          <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-300">
            <p>{t('upgradeToIncreaseLimits', { ns: 'subscription' })}</p>
          </div>
          <div className="mt-4">
            <div className="-mx-2 -my-1.5 flex">
              <Button
                variant="warning"
                className="bg-yellow-50 dark:bg-yellow-900/20 px-2 py-1.5 rounded-md text-sm font-medium text-yellow-800 dark:text-yellow-200 hover:bg-yellow-100 dark:hover:bg-yellow-900/40 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-yellow-50 focus:ring-yellow-600"
                onClick={onUpgrade || (() => window.location.href = '/subscription')}
              >
                {t('viewPlans', { ns: 'subscription' })}
              </Button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubscriptionContext; 